import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { enumKeyByValue, sumBy } from '@app/shared/helpers';
import { labelTopValue } from '@app/shared/helpers/labeling-helpers';
import {
  DenominationMultiplier,
  InstrumentType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { OpmForecastDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { FC, useMemo, useState } from 'react';
import { MAX_LABEL_ITEMS } from './constants';
import styles from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart.module.scss';
import {
  selectAllInstrumentsIncludingInitialValues,
  selectOrdinaryEquityInstruments,
  selectPrefShareInstruments,
  selectSlnInstruments,
} from '@app/core/store/capital-structure-slice-selectors';
import ChartLegend, { LegendItem } from './ChartLegend';
import HorizontalGridLines from './HorizontalGridLines';
import InstrumentBlocksByYear from './InstrumentBlocksByYear';
import { getStructureForForecastStack } from './utilities';
import {
  PerYearCapitalStructureChartStructure,
  excludeNetDebtFromChartStructure,
  transformOpmToChartStructure,
} from '@app/modules/projects/dashboard/shared';
import { PerYearCapitalStructureModal } from '@app/modules/projects/dashboard/shared/per-year-capital-structure/PerYearCapitalStructureModal';
const PARENT_CLASSNAME = 'total-equity-chart';

interface OpmTotalEquityChartProps {
  forecastData: OpmForecastDto[];
  entryData: OpmForecastDto;
}

const OpmTotalEquityChart: FC<OpmTotalEquityChartProps> = ({
  forecastData,
  entryData,
}): JSX.Element => {
  const { l } = useLocale();
  const allInstruments = useAppSelector(selectAllInstrumentsIncludingInitialValues);
  const slnInstruments = useAppSelector(selectSlnInstruments);
  const prefSharesInstruments = useAppSelector(selectPrefShareInstruments);
  const ordInstruments = useAppSelector(selectOrdinaryEquityInstruments);
  const denomination = useAppSelector((state) => state.project.projectDraft.details.denomination);
  const multiplier = DenominationMultiplier[denomination];
  const chartForecastsCount = forecastData.length;

  const [showModal, setShowModal] = useState(false);
  const [selectedIsEntry, setSelectedIsEntry] = useState(false);
  const [selectedForecastIndex, setSelectedForecastIndex] = useState<number | undefined>(undefined);

  const legendItems: LegendItem[] = [
    {
      iconModifier: 'sln',
      label: l('_ShareholderLoanNotesTitle'),
      visible: slnInstruments.length > 0,
    },
    {
      iconModifier: 'pref',
      label: l(`_${enumKeyByValue(InstrumentType, InstrumentType.PreferredShares)}`),
      visible: prefSharesInstruments.length > 0,
    },
    {
      iconModifier: 'ord',
      label: l('_OrdinaryEquityTitle'),
      visible: ordInstruments.some((i) => !i.isSweetEquity),
    },
    {
      iconModifier: 'sweet',
      label: l('_SweetEquityTitle'),
      visible: ordInstruments.some((i) => i.isSweetEquity),
    },
  ];

  const totalInvestments = sumBy(
    allInstruments.flatMap((x) =>
      x.initialValues?.ownership ? Object.values(x.initialValues.ownership) : []
    ),
    (x) => x.amount ?? 0
  );

  const stacksPerForecast = forecastData.map((f) =>
    getStructureForForecastStack(f.forecastDate, forecastData, allInstruments)
  );
  const totalEquityPerForecast = stacksPerForecast.map((x) =>
    sumBy(
      x.ranks.flatMap((r) => r.instrumentBlocks),
      (i) => i.amount
    )
  );

  const maxEquity = Math.max(totalInvestments, ...totalEquityPerForecast);

  const calculatedLabelTopValue = labelTopValue(maxEquity);

  const handleStackClick = (isEntry: boolean, forecastIndex?: number) => {
    setSelectedIsEntry(isEntry);
    setSelectedForecastIndex(forecastIndex);
    setShowModal(true);
  };

  const modalStructure: PerYearCapitalStructureChartStructure | null = useMemo(() => {
    if (
      selectedForecastIndex !== undefined &&
      selectedForecastIndex !== -1 &&
      selectedForecastIndex < forecastData.length
    ) {
      return excludeNetDebtFromChartStructure(
        transformOpmToChartStructure(forecastData[selectedForecastIndex])
      );
    } else if (selectedForecastIndex === -1) {
      return excludeNetDebtFromChartStructure(transformOpmToChartStructure(entryData));
    }
    return null;
  }, [selectedForecastIndex, forecastData, entryData]);

  return (
    <div aria-hidden className={styles[PARENT_CLASSNAME]}>
      <ChartLegend items={legendItems} />
      <div className={styles[`${PARENT_CLASSNAME}__chart`]}>
        <HorizontalGridLines
          labelTopValue={calculatedLabelTopValue}
          multiplier={multiplier}
          numberOfGridLines={MAX_LABEL_ITEMS}
        />
        <InstrumentBlocksByYear
          calculatedLabelTopValue={calculatedLabelTopValue}
          totalInvestments={totalInvestments}
          totalEquityPerForecast={totalEquityPerForecast}
          chartForecastsCount={chartForecastsCount}
          forecastData={forecastData}
          onStackClick={handleStackClick}
        />
      </div>
      <PerYearCapitalStructureModal
        structure={modalStructure}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        isEntryStack={selectedIsEntry}
        percentageBasis="netEquity"
      />
    </div>
  );
};

export default OpmTotalEquityChart;
